import storage from "./storage";

/**
 * Permission Utility for RBAC
 * Checks user permissions from localStorage
 */

/**
 * Get current user's permissions
 * @returns {Array} Array of permission strings (e.g., ["users.view", "roles.create"])
 */
export const getUserPermissions = () => {
  try {
    const userString = storage.getItem("user");
    if (!userString) return [];

    const user = JSON.parse(userString);
    return user.permissions || [];
  } catch (error) {
    console.error("Error getting user permissions:", error);
    return [];
  }
};

/**
 * Get current user's role
 * @returns {string} User role (e.g., "admin", "superadmin", "tst")
 */
export const getUserRole = () => {
  try {
    const userString = storage.getItem("user");
    if (!userString) return null;

    const user = JSON.parse(userString);
    return user.role || null;
  } catch (error) {
    console.error("Error getting user role:", error);
    return null;
  }
};

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission to check (e.g., "users.view")
 * @returns {boolean} True if user has the permission
 */
export const hasPermission = (permission) => {
  const role = getUserRole();

  // Superadmin has all permissions
  if (role === "superadmin") {
    return true;
  }

  const permissions = getUserPermissions();
  return permissions.includes(permission);
};

/**
 * Check if user has ANY of the specified permissions
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean} True if user has at least one permission
 */
export const hasAnyPermission = (permissions) => {
  const role = getUserRole();

  // Superadmin has all permissions
  if (role === "superadmin") {
    return true;
  }

  const userPermissions = getUserPermissions();
  return permissions.some((perm) => userPermissions.includes(perm));
};

/**
 * Check if user has ALL of the specified permissions
 * @param {Array<string>} permissions - Array of permissions to check
 * @returns {boolean} True if user has all permissions
 */
export const hasAllPermissions = (permissions) => {
  const role = getUserRole();

  // Superadmin has all permissions
  if (role === "superadmin") {
    return true;
  }

  const userPermissions = getUserPermissions();
  return permissions.every((perm) => userPermissions.includes(perm));
};

/**
 * Check if user can perform an action on a module
 * @param {string} module - Module name (e.g., "users")
 * @param {string} action - Action name (e.g., "create", "view", "update", "delete")
 * @returns {boolean} True if user can perform the action
 */
export const canAccess = (module, action) => {
  return hasPermission(`${module}.${action}`);
};

/**
 * Check if user is superadmin
 * @returns {boolean} True if user is superadmin
 */
export const isSuperAdmin = () => {
  return getUserRole() === "superadmin";
};

export default {
  getUserPermissions,
  getUserRole,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  canAccess,
  isSuperAdmin,
};
